import * as uuid from 'uuid';
import { Injectable } from '@angular/core';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, of, concat, from, throwError } from 'rxjs';
import { map, catchError, concatMap, toArray } from 'rxjs/operators';
import { RequestRoot, FeaturesFactory, FeaturesTreeReaderFactory, SearchOption, RequestBlob } from 'types';
import { Credentials, CredentialsFactory } from 'authorization';
import { RequestDataAccessService, RequestDataAccessServiceConfig } from './request-data-access.service';
import { CouchDbSequenceProvider } from './base/couchdb-sequence-provider';

@Injectable()
export class CouchRequestDataAccessService implements RequestDataAccessService {
  constructor(
    private http: HttpClient,
    private featuresF: FeaturesFactory,
    private featureTreeReaderF: FeaturesTreeReaderFactory,
    private config: RequestDataAccessServiceConfig,
    private credentialsFactory: CredentialsFactory,
    private couchDbSequenceProvider: CouchDbSequenceProvider,
  ) {}

  count(query?: any): Observable<number> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalCount(c, query)));
  }

  private internalCount(credentials: Credentials, query?: any): Observable<number> {
    const params: any = {};
    params.selector = {};
    if (query['selectors']) {
      Object.keys(query['selectors']).forEach((k) => {
        params.selector[k] = CouchRequestDataAccessService.createMangoSelector(query['selectors'][k]);
      });
    }
    params.limit = 10000;
    const url = this.config.getServerURL() + 'requests/_find';
    return this.http.post(url, params, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      map((response: any) => {
        if (!!response.error || !Array.isArray(response.docs)) {
          throw new Error('Request search error: ' + response.reason);
        }
        return response.docs.length;
      }),
    );
  }

  find(query: any): Observable<RequestRoot[]> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalFind(c, query)));
  }

  private internalFind(credentials: Credentials, query: any): Observable<RequestRoot[]> {
    const indexes: string[] = [];
    const params: any = {};
    params.selector = {};
    if (query['selectors']) {
      Object.keys(query['selectors']).forEach((k) => {
        params.selector[k] = CouchRequestDataAccessService.createMangoSelector(query['selectors'][k]);
      });
    }
    if (query['orders']) {
      params.sort = [];
      const list = query['orders'].ordering;
      for (let i = 0; i < list.length; ++i) {
        if (list[i].path) {
          const sortingPath: string = list[i].path;
          const sortingDirection = list[i].direction === 'ASC' ? 'asc' : 'desc';
          const orderingSetting: any = {};
          orderingSetting[sortingPath] = sortingDirection;
          params.sort.push(orderingSetting);
          indexes.push(sortingPath);
        }
      }
    }
    if (query['limits']) {
      params.skip = query['limits'].offset;
      params.limit = query['limits'].count;
    } else {
      params.limit = 10000;
      params.skip = 0;
    }
    return from(indexes).pipe(
      concatMap((index) => this.indexHelper(credentials, index)),
      toArray(),
      concatMap((x) => this.findHelper(credentials, params)),
    );
  }

  private indexHelper(credentials: Credentials, index: string): Observable<any> {
    const url = this.config.getServerURL() + 'requests/_index';
    const params = { index: { fields: [index] }, name: index + '-index', type: 'json' };
    return this.http.post(url, params, CouchRequestDataAccessService.createHttpOptions(credentials));
  }

  private findHelper(credentials: Credentials, params: any): Observable<RequestRoot[]> {
    const url = this.config.getServerURL() + 'requests/_find';
    return this.http.post(url, params, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      map((response: any) => {
        if (!!response.error || !Array.isArray(response.docs)) {
          throw new Error('Request find error: ' + response.reason);
        }
        return response.docs.map((row: any) => this.consumeRequestData(row));
      }),
    );
  }

  load(request: RequestRoot): Observable<RequestRoot> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalLoad(c, request.id!)));
  }

  private internalLoad(credentials: Credentials, requestId: string): Observable<RequestRoot> {
    const url = this.config.getServerURL() + 'requests/' + requestId + '?attachments=true';
    return this.http.get(url, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      map((response: any) => {
        if (!!response.error) {
          throw new Error('Request load error: ' + response.reason);
        }
        return this.consumeRequestData(response);
      }),
    );
  }

  createUID(): Observable<string> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalCreateUID(c)));
  }

  private internalCreateUID(credentials: Credentials): Observable<string> {
    const getDeptCode$ = this.couchDbSequenceProvider.getSequenceValue('dept_code');
    const getAmbulanceCode$ = this.couchDbSequenceProvider.getSequenceValue('ambulance_code');
    const uidSequenceURL = this.config.getServerURL() + 'sequences/request_uid';
    const rq3 = this.http.get(uidSequenceURL, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      catchError((e) => of({})),
      concatMap((x: any) => {
        const updateRequest: any = {};
        if (x._rev && x.value) {
          updateRequest._rev = x._rev;
          updateRequest.value = x.value + 1;
        } else {
          updateRequest.value = 1;
        }
        return this.http.put(uidSequenceURL, updateRequest, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
          map((updateResponce: any) => {
            if (updateResponce['ok']) {
              return updateRequest.value;
            }
            throw new Error('Sequence update error: ' + updateResponce.reason);
          }),
        );
      }),
    );
    return concat(getDeptCode$, getAmbulanceCode$, rq3).pipe(
      toArray(),
      map((x) => CouchRequestDataAccessService.formatRequestUID(x[0], x[1], x[2])),
    );
  }

  private upsert(credentials: Credentials, request: RequestRoot): Observable<void> {
    const url = this.config.getServerURL() + 'requests';
    const requestData = CouchRequestDataAccessService.prepareRequestData(request);
    return this.http.post(url, requestData, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      map((responce: any) => {
        if (responce['ok']) {
          request.revision = responce.rev;
          if (request.rawBlobs) {
            for (const blob of request.rawBlobs) {
              blob.dirty = false;
            }
          }
          if (request.imgBlobs) {
            for (const blob of request.imgBlobs) {
              blob.dirty = false;
            }
          }
          return;
        }
        throw new Error('Request upsert error: ' + responce.reason);
      }),
    );
  }

  save(request: RequestRoot): Observable<void> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalSave(c, request)));
  }

  private internalSave(credentials: Credentials, request: RequestRoot): Observable<void> {
    if (request.id) {
      return throwError('Cannot INSERT');
    }
    request.id = uuid.v4().toString();
    return this.upsert(credentials, request);
  }

  edit(request: RequestRoot): Observable<void> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalEdit(c, request)));
  }

  private internalEdit(credentials: Credentials, request: RequestRoot): Observable<void> {
    if (!request.id) {
      return throwError('Cannot UPDATE');
    }
    return this.upsert(credentials, request);
  }

  wipe(request: RequestRoot): Observable<void> {
    return this.credentialsFactory.getCredentials$().pipe(concatMap((c) => this.internalWipe(c, request)));
  }

  private internalWipe(credentials: Credentials, request: RequestRoot): Observable<void> {
    const url = this.config.getServerURL() + 'requests/' + request.id + '?rev=' + request.revision;
    return this.http.delete(url, CouchRequestDataAccessService.createHttpOptions(credentials)).pipe(
      map((responce: any) => {
        if (responce['ok']) {
          return;
        }
        throw new Error('Request wipe error: ' + responce.reason);
      }),
    );
  }

  private static prepareRequestAttachment(attachments: any, prefix: string, blob: RequestBlob) {
    if (blob.dirty) {
      if (blob.data) {
        attachments[prefix + blob.tag] = { content_type: 'application/octet-stream', data: blob.data };
      }
    } else {
      attachments[prefix + blob.tag] = { stub: true };
    }
  }

  private static prepareRequestData(_obj: RequestRoot): any {
    const result = _obj.writeObject();
    result.features = JSON.parse(result.features);
    if (result.id) {
      result._id = result.id;
    }
    delete result.id;
    if (result.revision) {
      result._rev = result.revision;
    }
    delete result.revision;
    result._attachments = {};
    for (const blob of result.rawBlobs) {
      CouchRequestDataAccessService.prepareRequestAttachment(result._attachments, 'RAW.', blob);
    }
    delete result.rawBlobs;
    for (const blob of result.imgBlobs) {
      CouchRequestDataAccessService.prepareRequestAttachment(result._attachments, 'IMG.', blob);
    }
    delete result.imgBlobs;
    return result;
  }

  private consumeRequestData(_obj: any): RequestRoot {
    const result = { id: _obj._id, revision: _obj._rev, rawBlobs: [], imgBlobs: [], ..._obj };
    result.features = JSON.stringify(result.features);
    for (const [key, value] of Object.entries(result._attachments || {})) {
      if (key.startsWith('RAW.')) {
        result.rawBlobs.push({ tag: key.substr(4), data: (value as any).data || '', dirty: false });
      }
      if (key.startsWith('IMG.')) {
        result.imgBlobs.push({ tag: key.substr(4), data: (value as any).data || '', dirty: false });
      }
    }
    delete result._id;
    delete result._rev;
    delete result._attachments;
    return RequestRoot.readObject(result, this.featuresF, this.featureTreeReaderF);
  }

  private static formatRequestUID(deptCode: string, ambulanceCode: string, sequence: number): string {
    const deptFormat = '2' + ('' + deptCode).padStart(8, '0');
    if (deptFormat.length !== 9) {
      throw new Error('Invalid dept format');
    }
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = ('' + (now.getMonth() + 1)).padStart(2, '0');
    const currentDay = ('' + now.getDate()).padStart(2, '0');
    const dateFormat = currentYear + currentMonth + currentDay;
    if (dateFormat.length !== 8) {
      throw new Error('Invalid date format');
    }
    const sequenceFormat = '0' + ambulanceCode + '9' + ('' + sequence).padStart(5, '0');
    if (dateFormat.length !== 8) {
      throw new Error('Invalid sequence format');
    }
    return deptFormat + dateFormat + sequenceFormat;
  }

  private static createHttpOptions(credentials: Credentials): { headers: HttpHeaders } {
    return {
      headers: new HttpHeaders({
        'Content-Type': 'application/json',
        'Accept': 'application/json',
        'Authorization': 'Basic ' + credentials.asEncodedString,
      }),
    };
  }

  private static createMangoSelector(selector: any): any {
    if (!selector.opt) {
      return { $eq: selector };
    }
    if (selector.opt.name === SearchOption.FirstLetters.name) {
      return { $regex: '(*UTF8)(?i)^' + selector.val + '.*' };
    } else if (selector.opt.name === SearchOption.Exact.name) {
      return { $regex: '(*UTF8)(?i)^' + selector.val + '$' };
    } else if (selector.opt.name === SearchOption.Partial.name) {
      return { $regex: '(*UTF8)(?i)' + selector.val };
    } else if (selector.opt.name === SearchOption.Interval.name) {
      const result: any = {};
      if (selector.val.begin) {
        result['$gte'] = selector.val.begin;
      }
      if (selector.val.end) {
        result['$lte'] = selector.val.end;
      }
      return result;
    } else if (selector.opt.name === SearchOption.SearchByEnum.name) {
      return { $in: selector.val };
    }
  }
}
